import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { useRuntimeHealthAccess } from "@/hooks/useRuntimeHealthAccess";
import { BellRing, PlayCircle, Save, Siren } from "lucide-react";

type Settings = {
  enabled: boolean;
  window_minutes: number;
  per_route_threshold: number;
  total_threshold: number;
  cooldown_minutes: number;
  notify_email: string | null;
  slack_webhook_url: string | null;
  escalate_after_minutes: number;
  escalate_repeat_minutes: number;
  escalate_max_level: number;
  escalation_slack_webhook: string | null;
  escalation_slack_bot_channel: string | null;
};

type AlertEvent = {
  id: string;
  created_at: string;
  kind: string;
  severity: string;
  window_start: string;
  window_end: string;
  counts: Record<string, unknown> | null;
  delivered_email: boolean;
  delivered_slack: boolean;
  error: string | null;
};

const DEFAULTS: Settings = {
  enabled: false,
  window_minutes: 15,
  per_route_threshold: 10,
  total_threshold: 50,
  cooldown_minutes: 60,
  notify_email: null,
  slack_webhook_url: null,
  escalate_after_minutes: 30,
  escalate_repeat_minutes: 30,
  escalate_max_level: 3,
  escalation_slack_webhook: null,
  escalation_slack_bot_channel: null,
};

export function FrontendErrorAlertsPanel() {
  const { toast } = useToast();
  const { canConfigure } = useRuntimeHealthAccess();
  const [settings, setSettings] = useState<Settings>(DEFAULTS);
  const [events, setEvents] = useState<AlertEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [{ data: s }, { data: ev }] = await Promise.all([
      supabase.from("frontend_error_alert_settings").select("*").eq("id", true).maybeSingle(),
      supabase
        .from("frontend_error_alert_events")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(20),
    ]);
    if (s) setSettings({ ...DEFAULTS, ...(s as unknown as Settings) });
    setEvents((ev ?? []) as unknown as AlertEvent[]);
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async () => {
    setSaving(true);
    const { error } = await supabase
      .from("frontend_error_alert_settings")
      .update({
        enabled: settings.enabled,
        window_minutes: Math.max(1, settings.window_minutes),
        per_route_threshold: Math.max(1, settings.per_route_threshold),
        total_threshold: Math.max(1, settings.total_threshold),
        cooldown_minutes: Math.max(1, settings.cooldown_minutes),
        notify_email: settings.notify_email?.trim() || null,
        slack_webhook_url: settings.slack_webhook_url?.trim() || null,
        escalate_after_minutes: Math.max(1, settings.escalate_after_minutes),
        escalate_repeat_minutes: Math.max(1, settings.escalate_repeat_minutes),
        escalate_max_level: Math.max(1, settings.escalate_max_level),
        escalation_slack_webhook: settings.escalation_slack_webhook?.trim() || null,
        escalation_slack_bot_channel: settings.escalation_slack_bot_channel?.trim() || null,
        updated_at: new Date().toISOString(),
      })
      .eq("id", true);
    setSaving(false);
    toast(
      error
        ? { title: "Could not save alert settings", description: error.message, variant: "destructive" }
        : { title: "Alert settings saved" },
    );
    if (!error) void load();
  };

  const runNow = async () => {
    setRunning(true);
    const { data, error } = await supabase.functions.invoke("frontend-error-alerts-monitor", {
      body: {},
    });
    setRunning(false);
    if (error) {
      toast({ title: "Check failed", description: error.message, variant: "destructive" });
      return;
    }
    const res = data as { triggered?: string[]; skipped?: string; counts?: { total?: number } };
    toast({
      title: res?.skipped
        ? `Check skipped (${res.skipped})`
        : res?.triggered?.length
          ? `Alert triggered: ${res.triggered.join(", ")}`
          : "No thresholds breached",
      description: `${res?.counts?.total ?? 0} errors in the current window.`,
    });
    void load();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BellRing className="size-4 text-primary" /> Error alerts
        </CardTitle>
        <CardDescription>
          Email/Slack alerts fire when a single page exceeds the per-page threshold inside the time
          window. A cooldown prevents repeat pings for the same spike.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center justify-between rounded-md border border-border p-3">
          <div>
            <p className="text-sm font-medium">Alerts enabled</p>
            <p className="text-xs text-muted-foreground">Checked automatically every 5 minutes.</p>
          </div>
          <Switch
            disabled={!canConfigure}
            checked={settings.enabled}
            onCheckedChange={(v) => setSettings((s) => ({ ...s, enabled: v }))}
            aria-label="Enable frontend error alerts"
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <NumberField
            id="fe-window"
            label="Window (minutes)"
            value={settings.window_minutes}
            onChange={(v) => setSettings((s) => ({ ...s, window_minutes: v }))}
          />
          <NumberField
            id="fe-route-threshold"
            label="Errors per page"
            value={settings.per_route_threshold}
            onChange={(v) => setSettings((s) => ({ ...s, per_route_threshold: v }))}
          />
          <NumberField
            id="fe-total-threshold"
            label="Total errors"
            value={settings.total_threshold}
            onChange={(v) => setSettings((s) => ({ ...s, total_threshold: v }))}
          />
          <NumberField
            id="fe-cooldown"
            label="Cooldown (minutes)"
            value={settings.cooldown_minutes}
            onChange={(v) => setSettings((s) => ({ ...s, cooldown_minutes: v }))}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="fe-email">Notify email</Label>
            <Input
              id="fe-email"
              type="email"
              placeholder="alerts@yourdomain.com"
              value={settings.notify_email ?? ""}
              onChange={(e) => setSettings((s) => ({ ...s, notify_email: e.target.value }))}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="fe-slack">Slack webhook URL</Label>
            <Input
              id="fe-slack"
              placeholder="https://hooks.slack.com/services/..."
              value={settings.slack_webhook_url ?? ""}
              onChange={(e) => setSettings((s) => ({ ...s, slack_webhook_url: e.target.value }))}
            />
          </div>
        </div>

        <div className="space-y-4 rounded-md border border-border p-3">
          <div className="flex items-center gap-2">
            <Siren className="size-4 text-primary" />
            <div>
              <p className="text-sm font-medium">Unacknowledged incident escalation</p>
              <p className="text-xs text-muted-foreground">
                If an incident stays unacknowledged past the window below, Slack gets an escalation
                message — as a threaded follow-up on the original post when a Slack bot token and
                channel are configured, otherwise as a new webhook message. Follow-ups repeat on the
                repeat interval up to the maximum level.
              </p>
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            <NumberField
              id="fe-escalate-after"
              label="Escalate after (minutes)"
              value={settings.escalate_after_minutes}
              onChange={(v) => setSettings((s) => ({ ...s, escalate_after_minutes: v }))}
            />
            <NumberField
              id="fe-escalate-repeat"
              label="Repeat every (minutes)"
              value={settings.escalate_repeat_minutes}
              onChange={(v) => setSettings((s) => ({ ...s, escalate_repeat_minutes: v }))}
            />
            <NumberField
              id="fe-escalate-max"
              label="Max escalation level"
              value={settings.escalate_max_level}
              onChange={(v) => setSettings((s) => ({ ...s, escalate_max_level: v }))}
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="fe-escalate-webhook">Escalation Slack webhook (fallback)</Label>
              <Input
                id="fe-escalate-webhook"
                placeholder="https://hooks.slack.com/services/..."
                value={settings.escalation_slack_webhook ?? ""}
                onChange={(e) => setSettings((s) => ({ ...s, escalation_slack_webhook: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="fe-escalate-channel">Slack channel ID (threads, needs bot token)</Label>
              <Input
                id="fe-escalate-channel"
                placeholder="C0123456789"
                value={settings.escalation_slack_bot_channel ?? ""}
                onChange={(e) => setSettings((s) => ({ ...s, escalation_slack_bot_channel: e.target.value }))}
              />
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button onClick={() => void save()} disabled={saving || loading || !canConfigure}>
            <Save className="size-4" /> {saving ? "Saving…" : "Save settings"}
          </Button>
          <Button variant="outline" onClick={() => void runNow()} disabled={running || !canConfigure}>
            <PlayCircle className="size-4" /> {running ? "Checking…" : "Run check now"}
          </Button>
        </div>

        <div className="space-y-2">
          <h4 className="text-sm font-medium">Recent alerts</h4>
          {events.length === 0 ? (
            <p className="text-sm text-muted-foreground">No alerts have fired yet.</p>
          ) : (
            events.map((e) => <AlertRow key={e.id} event={e} />)
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function AlertRow({ event }: { event: AlertEvent }) {
  const perRoute = (event.counts as { per_route?: Record<string, number> } | null)?.per_route ?? {};
  const top = useMemo(
    () => Object.entries(perRoute).sort((a, b) => b[1] - a[1]).slice(0, 3),
    [perRoute],
  );
  return (
    <div className="rounded-md border border-border p-3 text-sm">
      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <Badge variant={event.severity === "error" ? "destructive" : "secondary"}>{event.kind}</Badge>
        <span>{new Date(event.created_at).toLocaleString()}</span>
        <span>email: {event.delivered_email ? "sent" : "—"}</span>
        <span>slack: {event.delivered_slack ? "sent" : "—"}</span>
      </div>
      {top.length > 0 && (
        <p className="mt-2 font-mono text-xs">
          {top.map(([route, count]) => `${route} (${count})`).join(" · ")}
        </p>
      )}
      {event.error && <p className="mt-1 text-xs text-destructive">{event.error}</p>}
    </div>
  );
}

function NumberField({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: number;
  onChange: (value: number) => void;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type="number"
        min={1}
        value={value}
        onChange={(e) => onChange(Number(e.target.value) || 1)}
      />
    </div>
  );
}

export default FrontendErrorAlertsPanel;
